import { Mark, mergeAttributes } from '@tiptap/core';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import { Decoration, DecorationSet } from '@tiptap/pm/view';

/**
 * Custom TipTap extension for internal note linking with [[note name]] syntax
 */
export const NoteLink = Mark.create({
  name: 'noteLink',

  addOptions() {
    return {
      HTMLAttributes: {
        class: 'note-link',
      },
      onNoteLinkClick: null,
      getNotes: () => [],
    };
  },

  addAttributes() {
    return {
      noteId: {
        default: null,
      },
      noteName: {
        default: null,
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: 'span[data-note-link]',
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      'span',
      mergeAttributes(this.options.HTMLAttributes, HTMLAttributes, {
        'data-note-link': HTMLAttributes.noteId || '',
        'data-note-name': HTMLAttributes.noteName || '',
      }),
      0,
    ];
  },

  addProseMirrorPlugins() {
    const extension = this;

    return [
      new Plugin({
        key: new PluginKey('noteLinkDecorations'),
        props: {
          decorations(state) {
            const decorations = [];
            const doc = state.doc;
            
            // Find all [[note name]] patterns in the document
            const regex = /\[\[([^\]]+)\]\]/g;
            
            doc.descendants((node, pos) => {
              if (!node.isText) return;
              
              const text = node.text;
              let match;
              
              while ((match = regex.exec(text)) !== null) {
                const start = pos + match.index;
                const end = start + match[0].length;
                const noteName = match[1];
                
                // Find matching note
                const notes = extension.options.getNotes();
                const matchingNote = notes.find(
                  (n) => n.title?.toLowerCase() === noteName.toLowerCase()
                );
                
                decorations.push(
                  Decoration.inline(start, end, {
                    class: matchingNote ? 'note-link note-link-valid' : 'note-link note-link-invalid',
                    'data-note-id': matchingNote?.id || '',
                    'data-note-name': noteName,
                  })
                );
              }
            });
            
            return DecorationSet.create(doc, decorations);
          },
          
          handleClick(view, pos, event) {
            const target = event.target;
            if (target.classList?.contains('note-link')) {
              const noteId = target.getAttribute('data-note-id');
              const noteName = target.getAttribute('data-note-name');
              
              if (noteId && extension.options.onNoteLinkClick) {
                extension.options.onNoteLinkClick(noteId, noteName);
                return true;
              }
            }
            return false;
          },
        },
      }),
    ];
  },
});

export default NoteLink;
